import { WebSocketMessageType, type INotificationMessage } from "@src/models";

const BASE_URL = import.meta.env.VITE_EXTRACTION_NOTIFICATION_URL;
if (!BASE_URL) {
  throw new Error(
    "VITE_EXTRACTION_NOTIFICATION_URL is not set in your .env file.",
  );
}

interface WebSocketCallbacks {
  onMessage?: (message: INotificationMessage) => void;
  onError?: (error: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onOpen?: () => void;
  onAuthenticated?: () => void;
  onAuthenticationFailed?: (reason: string) => void;
}

class NotificationApi {
  private websockets: Map<string, WebSocket> = new Map();
  private isAuthenticated: Map<string, boolean> = new Map();
  private heartbeatIntervals: Map<string, number> = new Map();
  private authPromises: Map<
    string,
    { resolve: () => void; reject: (reason: string) => void }
  > = new Map();

  private getAccessToken(): string | null {
    try {
      return localStorage.getItem("accessToken");
    } catch (error) {
      console.error("Error reading access token from localStorage:", error);
      return null;
    }
  }

  /**
   * Connect to WebSocket and authenticate. Returns a Promise that resolves
   * once authentication succeeds, so callers can wait before triggering
   * backend processing (e.g. file upload).
   */
  public connectAndWaitForAuth(
    doctypeCode: string,
    processId: string,
    callbacks: WebSocketCallbacks = {},
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.authPromises.set(processId, { resolve, reject });
      this.connectToNotifications(doctypeCode, processId, {
        ...callbacks,
        onAuthenticated: () => {
          this.authPromises.delete(processId);
          callbacks.onAuthenticated?.();
          resolve();
        },
        onAuthenticationFailed: (reason: string) => {
          this.authPromises.delete(processId);
          callbacks.onAuthenticationFailed?.(reason);
          reject(reason);
        },
      });
    });
  }

  public connectToNotifications(
    doctypeCode: string,
    processId: string,
    callbacks: WebSocketCallbacks = {},
  ): void {
    if (this.websockets.has(processId)) {
      this.disconnect(processId);
    }

    const accessToken = this.getAccessToken();
    if (!accessToken) {
      const reason = "Cannot connect: Access token not found.";
      console.error(reason);
      callbacks.onAuthenticationFailed?.(reason);
      return;
    }

    const wsUrl = `${BASE_URL}?process_id=${encodeURIComponent(processId)}`;
    const websocket = new WebSocket(wsUrl);
    this.websockets.set(processId, websocket);
    this.isAuthenticated.set(processId, false);

    websocket.onopen = () => {
      callbacks.onOpen?.();
      this.authenticate(processId, doctypeCode, accessToken);
    };

    websocket.onmessage = (event) => {
      this.handleMessage(processId, event, callbacks);
    };

    websocket.onerror = (error) => {
      console.error(`WebSocket error for ${processId}:`, error);
      callbacks.onError?.(error);
    };

    websocket.onclose = (event) => {
      this.disconnect(processId, callbacks.onClose, event);
    };
  }

  private handleMessage(
    processId: string,
    event: MessageEvent,
    callbacks: WebSocketCallbacks,
  ) {
    try {
      const message: INotificationMessage = JSON.parse(event.data);
      switch (message.type) {
        case WebSocketMessageType.AuthenticationSuccess:
          this.isAuthenticated.set(processId, true);
          this.startHeartbeat(processId);
          callbacks.onAuthenticated?.();
          break;

        case WebSocketMessageType.AuthenticationFailed:
        case WebSocketMessageType.AuthenticationTimeout:
          this.isAuthenticated.set(processId, false);
          callbacks.onAuthenticationFailed?.(
            message.message || "Authentication failed",
          );
          this.disconnect(processId);
          break;

        case WebSocketMessageType.Pong:
          break;
      }

      callbacks.onMessage?.(message);
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
    }
  }

  private authenticate(
    processId: string,
    doctypeCode: string,
    accessToken: string,
  ) {
    const websocket = this.websockets.get(processId);
    if (!websocket || websocket.readyState !== WebSocket.OPEN) {
      console.error(`WebSocket for ${processId} not ready for authentication.`);
      return;
    }

    const authMessage = {
      type: WebSocketMessageType.Authentication,
      token: accessToken,
      doctype_code: doctypeCode,
      timestamp: new Date().toISOString(),
    };

    websocket.send(JSON.stringify(authMessage));
  }

  private startHeartbeat(processId: string) {
    this.stopHeartbeat(processId);
    const interval = window.setInterval(() => {
      this.sendPing(processId);
    }, 25000);
    this.heartbeatIntervals.set(processId, interval);
  }

  private stopHeartbeat(processId: string) {
    const interval = this.heartbeatIntervals.get(processId);
    if (interval) {
      clearInterval(interval);
      this.heartbeatIntervals.delete(processId);
    }
  }

  public sendPing(processId: string): void {
    const websocket = this.websockets.get(processId);
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      websocket.send(
        JSON.stringify({
          type: WebSocketMessageType.Ping,
          timestamp: new Date().toISOString(),
        }),
      );
    }
  }

  public isWebSocketAuthenticated(processId: string): boolean {
    return this.isAuthenticated.get(processId) || false;
  }

  public disconnect(
    processId: string,
    onClose?: (event: CloseEvent) => void,
    event?: CloseEvent,
  ): void {
    this.stopHeartbeat(processId);
    const websocket = this.websockets.get(processId);
    if (websocket) {
      websocket.onopen = null;
      websocket.onmessage = null;
      websocket.onerror = null;
      websocket.onclose = null;
      if (websocket.readyState < WebSocket.CLOSING) {
        websocket.close();
      }
    }

    this.websockets.delete(processId);
    this.isAuthenticated.delete(processId);

    if (event && onClose) {
      onClose(event);
    }
  }

  public disconnectAll(): void {
    const allProcessIds = Array.from(this.websockets.keys());
    allProcessIds.forEach((processId) => this.disconnect(processId));
  }
}

export const notificationApi = new NotificationApi();
